// Pure edits on a loop (everything the editor does to notes and mix). Results are always inside
// the rules (`@eb/shared` rules.ts): key-locked, clamped, sorted.
import {
  BASS_SLOT,
  clampMix,
  isInstrumentSlot,
  keyLockNote,
  LIMITS,
  LOOP_TICKS,
  NUM_LANE_SOUNDS,
  NUM_SOUNDS,
  altSound,
  loopVariant,
  MAX_PATTERNS,
  patternCount,
  patternEvents,
  sections,
  quantize,
  sortEvents,
  type Key,
  type LoopData,
  type NoteEvent,
  type SoundMix,
} from "@eb/shared";

export const sameNote = (a: Pick<NoteEvent, "pad" | "note" | "tick">, b: Pick<NoteEvent, "pad" | "note" | "tick">) =>
  a.pad === b.pad && a.tick === b.tick && (!isInstrumentSlot(a.pad) || a.note === b.note);

/** The note of `pad`/`note` starting inside the grid cell [tick, tick + grid), if any. */
export function noteInCell(
  events: NoteEvent[],
  pad: number,
  note: number,
  tick: number,
  grid: number,
): NoteEvent | undefined {
  return events.find(
    (e) => e.pad === pad && (!isInstrumentSlot(pad) || e.note === note) && e.tick >= tick && e.tick < tick + grid,
  );
}

export function addNote(loop: LoopData, e: NoteEvent, key: Key): LoopData {
  if (loop.events.length >= LIMITS.maxEvents) return loop;
  const tick = Math.max(0, Math.min(LOOP_TICKS - 1, Math.round(e.tick)));
  const ev: NoteEvent = {
    tick,
    pad: e.pad,
    note: isInstrumentSlot(e.pad) ? keyLockNote(e.note, e.pad, key) : 60,
    velocity: Math.max(1, Math.min(127, Math.round(e.velocity))),
    length_ticks: Math.max(1, Math.min(Math.round(e.length_ticks), LOOP_TICKS - tick)),
  };
  if (loop.events.some((x) => sameNote(x, ev))) return loop;
  return { ...loop, events: sortEvents([...loop.events, ev]) };
}

export function removeNote(loop: LoopData, target: Pick<NoteEvent, "pad" | "note" | "tick">): LoopData {
  const events = loop.events.filter((x) => !sameNote(x, target));
  return events.length === loop.events.length ? loop : { ...loop, events };
}

export function updateNote(
  loop: LoopData,
  target: Pick<NoteEvent, "pad" | "note" | "tick">,
  patch: Partial<Pick<NoteEvent, "velocity" | "length_ticks">>,
): LoopData {
  let changed = false;
  const events = loop.events.map((x) => {
    if (!sameNote(x, target)) return x;
    const velocity = patch.velocity !== undefined ? Math.max(1, Math.min(127, Math.round(patch.velocity))) : x.velocity;
    const length_ticks =
      patch.length_ticks !== undefined
        ? Math.max(1, Math.min(Math.round(patch.length_ticks), LOOP_TICKS - x.tick))
        : x.length_ticks;
    if (velocity === x.velocity && length_ticks === x.length_ticks) return x;
    changed = true;
    return { ...x, velocity, length_ticks };
  });
  return changed ? { ...loop, events } : loop;
}

/** Quantize note starts (optionally only `slots`) to `grid` with `strength` 0–1. */
export function quantizeLoop(loop: LoopData, grid: number, strength: number, slots?: number[]): LoopData {
  const moved = loop.events.map((e) => {
    if (slots && !slots.includes(e.pad)) return e;
    const tick = quantize(e.tick, grid, strength, LOOP_TICKS);
    return tick === e.tick ? e : { ...e, tick, length_ticks: Math.min(e.length_ticks, LOOP_TICKS - tick) };
  });
  // Notes that land on the same spot merge (keep the louder).
  const best = new Map<string, NoteEvent>();
  for (const e of moved) {
    const k = `${e.pad}:${isInstrumentSlot(e.pad) ? e.note : 60}:${e.tick}`;
    const prev = best.get(k);
    if (!prev || prev.velocity < e.velocity) best.set(k, e);
  }
  return { ...loop, events: sortEvents([...best.values()]) };
}

export function setMix(loop: LoopData, slot: number, patch: Partial<SoundMix>, defaults: SoundMix[]): LoopData {
  // 20 sounds, or 28 once the song has extra lanes.
  const length = loop.lanes?.length || slot >= NUM_SOUNDS ? NUM_LANE_SOUNDS : NUM_SOUNDS;
  const mix =
    loop.mix.length === length ? loop.mix.slice() : Array.from({ length }, (_, i) => loop.mix[i] ?? defaults[i]);
  mix[slot] = clampMix({ ...mix[slot], ...patch });
  return { ...loop, mix };
}

export function clearSlot(loop: LoopData, slot: number): LoopData {
  return { ...loop, events: loop.events.filter((e) => e.pad !== slot) };
}

/** Choose instrument `slot`'s sound: 0 = the day's main, 1–2 = its alternates. */
export function setVariant(l: LoopData, slot: number, variant: number): LoopData {
  if (loopVariant(l, slot) === variant) return l;
  // One entry per instrument (bass, harmony, lead, classic).
  const variants = Array.from({ length: NUM_SOUNDS - BASS_SLOT }, (_, i) =>
    i === slot - BASS_SLOT ? variant : loopVariant(l, BASS_SLOT + i),
  );
  if (variants.every((v) => v === 0)) {
    const { variants: _drop, ...rest } = l;
    return rest;
  }
  return { ...l, variants };
}

/** The extra lanes instrument `slot` could still add: its alternates, except the one its main
 *  lane already plays. */
export function addableLanes(l: LoopData, slot: number): number[] {
  const main = loopVariant(l, slot);
  return [1, 2]
    .filter((v) => v !== main)
    .map((v) => altSound(slot, v))
    .filter((p) => !l.lanes?.includes(p));
}

/** Add an extra instrument lane (sound 20–27) to the song; its mix starts at the sound's default. */
export function addLane(l: LoopData, sound: number, defaults: SoundMix[]): LoopData {
  if (sound < NUM_SOUNDS || sound >= NUM_LANE_SOUNDS || l.lanes?.includes(sound)) return l;
  const mix = Array.from({ length: NUM_LANE_SOUNDS }, (_, i) => l.mix[i] ?? defaults[i]);
  return { ...l, mix, lanes: [...(l.lanes ?? []), sound].sort((a, b) => a - b) };
}

/** Remove an extra lane and its notes in every pattern. */
export function removeLane(l: LoopData, sound: number): LoopData {
  if (!l.lanes?.includes(sound)) return l;
  const keep = (events: NoteEvent[]) => events.filter((e) => e.pad !== sound);
  const lanes = l.lanes.filter((p) => p !== sound);
  const out: LoopData = { ...l, events: keep(l.events), lanes };
  if (l.extra_patterns) out.extra_patterns = l.extra_patterns.map(keep);
  if (lanes.length === 0) {
    delete out.lanes;
    out.mix = l.mix.slice(0, NUM_SOUNDS);
  }
  return out;
}

/** Add a pattern (empty, or a copy of pattern `copyOf`) at the end; ≤ 4 patterns. */
export function addPattern(l: LoopData, copyOf: number | null): LoopData {
  const n = patternCount(l);
  if (n >= MAX_PATTERNS) return l;
  const events = copyOf === null ? [] : patternEvents(l, copyOf).map((e) => ({ ...e }));
  return { ...l, extra_patterns: [...(l.extra_patterns ?? []), events], arrangement: sections(l) };
}

/** Delete pattern `i` (not the only one): sections that played it play A; later ones shift. */
export function deletePattern(l: LoopData, i: number): LoopData {
  const n = patternCount(l);
  if (n <= 1 || i < 0 || i >= n) return l;
  const all = Array.from({ length: n }, (_, k) => patternEvents(l, k)).filter((_, k) => k !== i);
  const arrangement = sections(l).map((p) => (p === i ? 0 : p > i ? p - 1 : p));
  const [events, ...extra] = all;
  const out: LoopData = { ...l, events };
  delete out.extra_patterns;
  delete out.arrangement;
  if (extra.length) {
    out.extra_patterns = extra;
    out.arrangement = arrangement;
  }
  return out;
}

/** Section `k` (0–3) plays pattern `p`. */
export function setSection(l: LoopData, k: number, p: number): LoopData {
  if (p < 0 || p >= patternCount(l)) return l;
  const arrangement = sections(l);
  if (arrangement[k] === p) return l;
  arrangement[k] = p;
  return { ...l, arrangement };
}
